import { useEffect, useMemo, useState } from "react";
import { Check, Globe, Loader2, Plus, Send, TrendingUp, X } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { dayKeyMtl } from "../../lib/dateMtl";

const RANGES = [1, 14, 30, 60, 90, 180, 365];

// Une couleur par moteur de recherche (couleurs de marque) — inclut les assistants IA
const ENGINE_COLORS = {
  Google: "#4285F4", Bing: "#0C8484", DuckDuckGo: "#DE5833", Yahoo: "#a558e0",
  AOL: "#39c2d7", Ask: "#d0342c", Qwant: "#5c97ff", Ecosia: "#3d8c40",
  Brave: "#fb542b", Lilo: "#66c2a3", Startpage: "#6573ff", Baidu: "#2932e1",
  Yandex: "#fc3f1d", Swisscows: "#d14836", Mojeek: "#7ab648", Naver: "#03c75a",
  ChatGPT: "#10a37f", Perplexity: "#20b8cd", Copilot: "#7bb8f5",
  "You.com": "#9b7bf5", Gemini: "#8e9bf5",
};
const engineColor = (e) => ENGINE_COLORS[e] || "#8a8578";

const posClass = (p) => {
  if (p == null) return "text-ivory/40";
  if (p <= 3) return "text-emerald-400 font-bold";
  if (p <= 10) return "text-emerald-300";
  if (p <= 30) return "text-amber-400";
  return "text-ivory/60";
};

const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short" });

const TOOLTIP_STYLE = {
  background: "#14120d", border: "1px solid rgba(212,175,55,0.35)",
  borderRadius: 12, fontSize: 12, color: "#f4f1ea",
};

// Carte « Suivi SEO » de l'aperçu admin : positions Google JOURNALIÈRES (Search
// Console) + visites quotidiennes reçues de chaque moteur (notre analytics),
// une couleur par moteur, période au choix (1 à 365 jours).
export default function SeoPositions() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);       // positions + mots-clés cibles (GSC)
  const [daily, setDaily] = useState(null);     // positions journalières (GSC)
  const [engines, setEngines] = useState(null); // visites par moteur (analytics)
  const [kw, setKw] = useState(null);           // recherches par mot-clé, TOUS moteurs
  const [prefs, setPrefs] = useState(null);     // préférences rapports courriel
  const [reportBusy, setReportBusy] = useState("");
  const [kwBusy, setKwBusy] = useState("");     // mot-clé en cours d'ajout/retrait
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/analytics/engines", { params: { days } })
      .then((r) => { if (alive) setEngines(r.data); })
      .catch(() => {});
    api.get("/admin/seo/keywords", { params: { days } })
      .then((r) => { if (alive) setKw(r.data); })
      .catch(() => {});
    api.get("/admin/gsc/daily", { params: { days } })
      .then((r) => { if (alive) setDaily(r.data); })
      .catch(() => {});
    api.get("/admin/gsc/positions", { params: { days } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  useEffect(() => {
    api.get("/admin/seo-report/prefs").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await api.put("/admin/seo-report/prefs", next);
      toast.success("Préférences des rapports enregistrées");
    } catch {
      toast.error("Enregistrement impossible");
    }
  };

  const sendReportNow = async (kind) => {
    setReportBusy(kind);
    try {
      const { data } = await api.post(`/admin/seo-report/${kind}/now`);
      toast.success(data.emailed
        ? `Rapport envoyé à info@trophee.ca (${data.periode})`
        : "Rapport généré (courriel non envoyé)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setReportBusy("");
    }
  };

  const connected = !!(data && data.connected);

  // Mots-clés cibles déjà suivis (normalisés) — une suggestion suivie affiche une coche
  const tracked = new Set((kw?.target_keywords || []).map((k) => k.trim().toLowerCase()));

  // Ajout en 1 clic d'une suggestion (IA ou opportunité) aux mots-clés cibles
  const addKeyword = async (keyword) => {
    setKwBusy(keyword);
    try {
      const { data: res } = await api.post("/admin/seo/target-keywords", { keyword });
      setKw((prev) => prev ? { ...prev, target_keywords: res.keywords } : prev);
      toast.success(res.already
        ? `« ${keyword} » était déjà suivi`
        : `« ${keyword} » ajouté aux mots-clés cibles — position visible sous peu`);
      // Rafraîchit le tableau des cibles (si Google est connecté), sans bloquer
      if (connected) {
        api.get("/admin/gsc/positions", { params: { days } })
          .then((r) => setData(r.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Ajout impossible");
    } finally {
      setKwBusy("");
    }
  };

  // « Tout suivre » : ajoute TOUTES les suggestions (opportunités + idées IA) en 1 clic
  const addAllKeywords = async () => {
    const all = [
      ...(kw?.suggestions?.opportunities || []).map((o) => o.q),
      ...(kw?.suggestions?.ideas || []),
    ].filter((k) => !tracked.has(k.trim().toLowerCase()));
    if (!all.length) {
      toast.info("Toutes les suggestions sont déjà suivies");
      return;
    }
    setKwBusy("bulk");
    try {
      const { data: res } = await api.post("/admin/seo/target-keywords/bulk", { keywords: all });
      setKw((prev) => prev ? { ...prev, target_keywords: res.keywords } : prev);
      toast.success(res.added
        ? `${res.added} mots-clés ajoutés au suivi — positions visibles sous peu`
        : "Toutes les suggestions étaient déjà suivies");
      if (connected) {
        api.get("/admin/gsc/positions", { params: { days } })
          .then((r) => setData(r.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Ajout impossible");
    } finally {
      setKwBusy("");
    }
  };

  // Retire un mot-clé cible personnalisé (petit × dans le tableau des cibles)
  const removeKeyword = async (keyword) => {
    setKwBusy(keyword);
    try {
      await api.delete("/admin/seo/target-keywords", { params: { keyword } });
      setData((prev) => prev ? { ...prev, targets: prev.targets.filter((t) => t.keyword !== keyword) } : prev);
      setKw((prev) => prev
        ? { ...prev, target_keywords: (prev.target_keywords || []).filter((k) => k !== keyword) }
        : prev);
      toast.success(`« ${keyword} » retiré des mots-clés cibles`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Retrait impossible");
    } finally {
      setKwBusy("");
    }
  };

  // Séries graphiques avec toutes les journées de la période (0 si rien)
  const engineChart = useMemo(() => {
    if (!engines) return [];
    const byDay = Object.fromEntries(engines.series.map((s) => [s.day, s]));
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = dayKeyMtl(d);
      out.push({ day: iso, label: dayLabel(iso), ...(byDay[iso] || {}) });
    }
    return out;
  }, [engines, days]);

  const dailyChart = useMemo(() => {
    if (!daily?.connected) return [];
    const byDay = Object.fromEntries(daily.series.map((s) => [s.day, s]));
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - 3 - i);  // Google finalise avec ~3 jours de délai
      const iso = dayKeyMtl(d);
      const row = byDay[iso];
      out.push({ day: iso, label: dayLabel(iso), position: row ? row.position : null, clics: row ? row.clicks : 0 });
    }
    return out.filter((r) => r.position != null || days <= 30);
  }, [daily, days]);

  const tickEvery = days > 90 ? 20 : days > 30 ? 9 : days > 14 ? 3 : 1;

  const connect = async () => {
    try {
      const { data } = await api.get("/admin/gsc/connect");
      window.location.href = data.url;
    } catch { /* non configuré */ }
  };

  // Soumet le sitemap dynamique à Google Search Console (indexation accélérée)
  const submitSitemap = async () => {
    setKwBusy("sitemap");
    try {
      const { data: res } = await api.post("/admin/gsc/submit-sitemap");
      toast.success(`Sitemap soumis à Google : ${res.sitemap}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Soumission impossible");
    } finally {
      setKwBusy("");
    }
  };

  return (
    <div className="mt-8 bg-surface border border-gold/15 rounded-xl p-7" data-testid="seo-positions">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Globe className="w-4 h-4 text-gold" />
        <h3 className="text-xs uppercase tracking-widest text-ivory/70">Suivi SEO — moteurs de recherche</h3>
        {connected && data && (
          <span className="text-[11px] text-ivory/50">{data.start} → {data.end} · Google finalise ses données avec ~3 jours de délai</span>
        )}
        {connected && (
          <button onClick={submitSitemap} disabled={kwBusy === "sitemap"} data-testid="seo-sitemap-submit"
            title="Soumettre le sitemap (toutes les pages) à Google Search Console"
            className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">
            {kwBusy === "sitemap" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Sitemap → Google
          </button>
        )}
        <div className="ml-auto flex flex-wrap gap-1.5" data-testid="seo-range-picker">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`seo-range-${r}`}
              className={`px-3 py-1.5 rounded-full border text-xs transition-colors duration-300 ${
                days === r ? "bg-gold text-ink border-gold font-bold"
                           : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
              {r === 1 ? "1 j" : `${r} j`}
            </button>
          ))}
        </div>
      </div>

      {/* Visites journalières par moteur — une couleur par moteur */}
      <p className="text-[11px] uppercase tracking-widest text-gold mb-3">Visites reçues des moteurs de recherche, par jour</p>
      {!engines ? (
        <div className="h-44 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gold" /></div>
      ) : engines.engines.length === 0 ? (
        <p className="text-xs text-ivory/60 mb-6" data-testid="seo-engines-empty">
          Aucune visite d'un moteur de recherche sur la période — les couleurs apparaîtront dès les premières visites (Google, Bing, DuckDuckGo…).
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="seo-engine-totals">
            {(engines.totals || []).map((t) => (
              <span key={t.engine} data-testid={`seo-engine-${t.engine.toLowerCase()}`}
                className="inline-flex items-center gap-2 text-xs border rounded-full px-3 py-1"
                style={{ borderColor: `${engineColor(t.engine)}55`, color: engineColor(t.engine) }}>
                <span className="w-2 h-2 rounded-full" style={{ background: engineColor(t.engine) }} />
                {t.engine} · {t.visites}
              </span>
            ))}
          </div>
          <div className="h-48 mb-7" data-testid="seo-engines-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={engineChart} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
                <CartesianGrid vertical={false} stroke="rgba(244,241,234,0.07)" />
                <XAxis dataKey="label" interval={tickEvery - 1 < 0 ? 0 : tickEvery - 1}
                  tick={{ fill: "rgba(244,241,234,0.55)", fontSize: 10 }}
                  axisLine={{ stroke: "rgba(244,241,234,0.15)" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "rgba(244,241,234,0.55)", fontSize: 10 }}
                  axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.day || ""} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {engines.engines.map((e) => (
                  <Line key={e} type="monotone" dataKey={e} stroke={engineColor(e)} strokeWidth={2}
                    dot={days <= 30} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Recherches par mot-clé — TOUS moteurs, TOUJOURS affiché (Bing fonctionne
          même sans connexion Google) */}
      <p className="text-[11px] uppercase tracking-widest text-gold mb-3">Recherches par mot-clé</p>
      {!kw ? (
        <div className="h-16 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-gold" /></div>
      ) : (
        <>
          {(() => {
            const rows = [
              ...(kw.google || []).map((q) => ({ ...q, engine: "Google" })),
              ...(kw.bing || []).map((q) => ({ ...q, engine: "Bing" })),
            ].sort((a, b) => b.impressions - a.impressions).slice(0, 40);
            return rows.length === 0 ? (
              <p className="text-xs text-ivory/60 mb-6" data-testid="seo-keywords-empty">
                Aucune recherche par mot-clé enregistrée sur la période.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-64 overflow-y-auto mb-7">
                <table className="w-full text-sm" data-testid="seo-keywords-table">
                  <thead className="sticky top-0 bg-surface">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
                      <th className="pb-2 font-medium">Moteur</th>
                      <th className="pb-2 font-medium">Mot-clé</th>
                      <th className="pb-2 font-medium text-right">Position</th>
                      <th className="pb-2 font-medium text-right">Impressions</th>
                      <th className="pb-2 font-medium text-right">Clics</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.engine}-${r.q}`} className="border-b border-ivory/5 last:border-0" data-testid={`seo-kw-${i}`}>
                        <td className="py-2 pr-3">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: engineColor(r.engine) }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: engineColor(r.engine) }} />
                            {r.engine}
                          </span>
                        </td>
                        <td className="py-2 text-ivory/85 pr-4">{r.q}</td>
                        <td className={`py-2 text-right ${posClass(r.position)}`}>{r.position != null ? `#${r.position}` : "—"}</td>
                        <td className="py-2 text-right text-ivory/60">{r.impressions}</td>
                        <td className="py-2 text-right text-ivory/60">{r.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Suggestions de mots-clés que les clients cherchent */}
          {kw.suggestions && (kw.suggestions.opportunities.length > 0 || kw.suggestions.ideas.length > 0) && (
            <div className="mb-7 border border-gold/20 bg-gold/5 rounded-lg p-5" data-testid="seo-suggestions">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <p className="text-[11px] uppercase tracking-widest text-gold">
                  Suggestions de mots-clés que les clients cherchent
                </p>
                <button type="button" data-testid="seo-suggestions-add-all"
                  onClick={addAllKeywords} disabled={!!kwBusy}
                  className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">
                  {kwBusy === "bulk" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Tout suivre
                </button>
              </div>
              {kw.suggestions.opportunities.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-ivory/60 mb-2">Déjà recherchés, mais au-delà de la 1re page — à travailler en priorité :</p>
                  <div className="flex flex-wrap gap-2">
                    {kw.suggestions.opportunities.map((q) => {
                      const isTracked = tracked.has(q.q.trim().toLowerCase());
                      return (
                        <span key={q.q} data-testid={`seo-opp-${q.q.replace(/\s+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-ink border border-amber-500/40 text-amber-300 rounded-full px-3 py-1">
                          {q.q} <span className="text-ivory/50">#{q.position} · {q.impressions} vues</span>
                          {isTracked ? (
                            <Check size={12} className="text-emerald-400" aria-label="Déjà suivi" />
                          ) : (
                            <button data-testid={`seo-opp-add-${q.q.replace(/\s+/g, "-")}`}
                              onClick={() => addKeyword(q.q)} disabled={!!kwBusy}
                              title="Suivre ce mot-clé (position Google dans le tableau des cibles)"
                              className="ml-0.5 text-gold hover:text-ink hover:bg-gold rounded-full p-0.5 transition-colors disabled:opacity-40">
                              {kwBusy === q.q ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {kw.suggestions.ideas.length > 0 && (
                <div>
                  <p className="text-xs text-ivory/60 mb-2">Idées de nouveaux mots-clés (IA) — cliquez + pour les suivre :</p>
                  <div className="flex flex-wrap gap-2">
                    {kw.suggestions.ideas.map((k) => {
                      const isTracked = tracked.has(k.trim().toLowerCase());
                      return (
                        <span key={k} data-testid={`seo-idea-${k.replace(/\s+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-ink border border-gold/40 text-gold rounded-full px-3 py-1">
                          {k}
                          {isTracked ? (
                            <Check size={12} className="text-emerald-400" aria-label="Déjà suivi" />
                          ) : (
                            <button data-testid={`seo-idea-add-${k.replace(/\s+/g, "-")}`}
                              onClick={() => addKeyword(k)} disabled={!!kwBusy}
                              title="Suivre ce mot-clé (position Google dans le tableau des cibles)"
                              className="ml-0.5 text-gold hover:text-ink hover:bg-gold rounded-full p-0.5 transition-colors disabled:opacity-40">
                              {kwBusy === k ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Sections Google Search Console (positions réelles) */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gold" /></div>
      ) : !connected ? (
        <div className="flex flex-wrap items-center gap-4 border-t border-ivory/10 pt-6" data-testid="seo-not-connected">
          <p className="text-sm text-ivory/60">
            Connectez Google Search Console pour voir vos POSITIONS réelles jour par jour (requêtes, classement, mots-clés cibles).
          </p>
          <button onClick={connect} data-testid="seo-connect-btn"
            className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2.5 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300">
            <Globe size={14} /> Connecter Google Search Console
          </button>
        </div>
      ) : (
        <>
          <p className="text-[11px] uppercase tracking-widest text-gold mb-3">Position moyenne Google, par jour</p>
          {dailyChart.length === 0 ? (
            <p className="text-xs text-ivory/60 mb-6" data-testid="seo-daily-empty">Pas encore de données journalières Google sur la période.</p>
          ) : (
            <div className="h-48 mb-7" data-testid="seo-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyChart} margin={{ top: 4, right: 4, bottom: 0, left: -14 }}>
                  <CartesianGrid vertical={false} stroke="rgba(244,241,234,0.07)" />
                  <XAxis dataKey="label" interval={tickEvery - 1 < 0 ? 0 : tickEvery - 1}
                    tick={{ fill: "rgba(244,241,234,0.55)", fontSize: 10 }}
                    axisLine={{ stroke: "rgba(244,241,234,0.15)" }} tickLine={false} />
                  {/* position 1 = en haut : axe inversé */}
                  <YAxis reversed domain={[1, "auto"]} allowDecimals={false}
                    tick={{ fill: "rgba(244,241,234,0.55)", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE}
                    formatter={(v, name) => [name === "position" ? `#${v}` : v, name === "position" ? "Position moyenne" : "Clics"]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.day || ""} />
                  <Line type="monotone" dataKey="position" stroke="#d4af37" strokeWidth={2}
                    dot={days <= 30} connectNulls name="position" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="seo-totals">
            {[
              { label: "Clics", value: data.clicks, tid: "clics" },
              { label: "Impressions", value: data.impressions, tid: "impressions" },
              { label: "Taux de clic", value: `${data.ctr} %`, tid: "ctr" },
              { label: "Position moyenne", value: data.position ? `#${data.position}` : "—", tid: "position" },
            ].map((s) => (
              <div key={s.tid} className="bg-ink border border-ivory/10 rounded-lg p-4 text-center">
                <p className="font-serif text-2xl text-champagne" data-testid={`seo-total-${s.tid}`}>{s.value}</p>
                <p className="text-[10px] uppercase tracking-widest text-ivory/50 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <p className="text-[11px] uppercase tracking-widest text-gold mb-3 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Mots-clés cibles — où en sommes-nous
          </p>
          <div className="overflow-x-auto mb-7">
            <table className="w-full text-sm" data-testid="seo-targets-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
                  <th className="pb-2 font-medium">Mot-clé</th>
                  <th className="pb-2 font-medium text-right">Position</th>
                  <th className="pb-2 font-medium text-right">Impressions</th>
                  <th className="pb-2 font-medium text-right">Clics</th>
                </tr>
              </thead>
              <tbody>
                {data.targets.map((t) => (
                  <tr key={t.keyword} className="border-b border-ivory/5 last:border-0" data-testid={`seo-target-${t.keyword.replace(/\s+/g, "-")}`}>
                    <td className="py-2 text-ivory/85">
                      {t.keyword}
                      {t.custom && (
                        <button data-testid={`seo-target-remove-${t.keyword.replace(/\s+/g, "-")}`}
                          onClick={() => removeKeyword(t.keyword)} disabled={!!kwBusy}
                          title="Retirer ce mot-clé des cibles suivies"
                          className="ml-2 align-middle text-ivory/40 hover:text-red-400 rounded-full transition-colors disabled:opacity-40">
                          {kwBusy === t.keyword ? <Loader2 size={11} className="animate-spin inline" /> : <X size={11} className="inline" />}
                        </button>
                      )}
                    </td>
                    <td className={`py-2 text-right ${posClass(t.position)}`}>
                      {t.position != null ? `#${t.position}` : "—"}
                    </td>
                    <td className="py-2 text-right text-ivory/60">{t.impressions}</td>
                    <td className="py-2 text-right text-ivory/60">{t.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] uppercase tracking-widest text-gold mb-3">Requêtes qui vous affichent dans Google</p>
          {data.queries.length === 0 ? (
            <p className="text-sm text-ivory/60" data-testid="seo-no-queries">Aucune requête enregistrée sur la période.</p>
          ) : (
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-sm" data-testid="seo-queries-table">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
                    <th className="pb-2 font-medium">Requête</th>
                    <th className="pb-2 font-medium text-right">Position</th>
                    <th className="pb-2 font-medium text-right">Impressions</th>
                    <th className="pb-2 font-medium text-right">Clics</th>
                  </tr>
                </thead>
                <tbody>
                  {data.queries.map((r, i) => (
                    <tr key={r.q} className="border-b border-ivory/5 last:border-0" data-testid={`seo-query-${i}`}>
                      <td className="py-2 text-ivory/85 pr-4">{r.q}</td>
                      <td className={`py-2 text-right ${posClass(r.position)}`}>#{r.position}</td>
                      <td className="py-2 text-right text-ivory/60">{r.impressions}</td>
                      <td className="py-2 text-right text-ivory/60">{r.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-ivory/55 mt-4">
            Vert = première page (#1-10) · ambre = #11-30 · « — » = pas encore classé sur ce mot-clé.
          </p>
        </>
      )}

      {/* Rapports par courriel : journalier (8 h) / hebdo (lundi 8 h) + envoi immédiat */}
      <div className="mt-7 pt-5 border-t border-ivory/10 flex flex-wrap items-center gap-3" data-testid="seo-report-controls">
        <p className="text-[11px] uppercase tracking-widest text-ivory/60 w-full">Rapports SEO par courriel (à info@trophee.ca)</p>
        {prefs && (
          <>
            <button onClick={() => savePrefs({ ...prefs, daily: !prefs.daily })} data-testid="seo-pref-daily"
              className={`px-4 py-1.5 rounded-full border text-xs transition-colors duration-300 ${
                prefs.daily ? "bg-gold text-ink border-gold font-bold"
                            : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
              Journalier · 8 h {prefs.daily ? "✓" : ""}
            </button>
            <button onClick={() => savePrefs({ ...prefs, weekly: !prefs.weekly })} data-testid="seo-pref-weekly"
              className={`px-4 py-1.5 rounded-full border text-xs transition-colors duration-300 ${
                prefs.weekly ? "bg-gold text-ink border-gold font-bold"
                             : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
              Hebdo · lundi 8 h {prefs.weekly ? "✓" : ""}
            </button>
          </>
        )}
        <span className="flex-1" />
        <button onClick={() => sendReportNow("daily")} disabled={!!reportBusy} data-testid="seo-send-daily-now"
          className="inline-flex items-center gap-2 border border-ivory/25 text-ivory/80 px-4 py-1.5 rounded-full text-xs hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
          {reportBusy === "daily" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Rapport d'hier maintenant
        </button>
        <button onClick={() => sendReportNow("weekly")} disabled={!!reportBusy} data-testid="seo-send-weekly-now"
          className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-1.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
          {reportBusy === "weekly" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Rapport de la semaine maintenant
        </button>
      </div>
    </div>
  );
}
